import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Sparkles, UserPlus, CreditCard } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { JoinForm } from '../components/join/JoinForm';
import { JoinSuccess } from '../components/join/JoinSuccess';
import { PerksSection } from '../components/join/PerksSection';
import { QRPoster } from '../components/join/QRPoster';
import { StatPills, Leaderboard, useJoinStats } from '../components/join/LiveStats';
import { API } from '../components/join/joinConfig';
import { CampusLeaderboard } from '../components/join/CampusLeaderboard';
import { ScanPayCard } from '../components/join/PaymentPanel';

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/87e159dc-3189-4727-ae1e-bf728f7f511e/images/d98ccae899d3e6698cb801a93f0a50973b9a78654e907687d2ed68a3faac7ce3.jpeg";

export default function Join() {
  const { t } = useLanguage();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const refCode = (params.get('ref') || '').toUpperCase();
  const [referrer, setReferrer] = useState(null);
  const [done, setDone] = useState(null);
  const { stats, board } = useJoinStats();

  useEffect(() => {
    if (refCode) fetch(`${API}/ref/${refCode}`).then(r => (r.ok ? r.json() : null)).then(setReferrer).catch(() => {});
  }, [refCode]);

  return (
    <div className="min-h-screen bg-[#FFFBF5] text-gray-900" data-testid="join-page">
      <div className="flex h-1.5"><div className="flex-1 bg-party-red" /><div className="flex-1 bg-white" /><div className="flex-1 bg-party-green" /></div>
      <section className="relative overflow-hidden">
        <img src={HERO_IMG} alt="" className="absolute inset-0 h-full w-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#FFFBF5] via-[#FFFBF5]/95 to-[#FFFBF5]/60" />
        <div className="relative mx-auto grid max-w-7xl gap-10 px-5 py-14 sm:py-20 lg:grid-cols-2 lg:items-center">
          <div className="join-reveal min-w-0">
            {referrer && (
              <div className="mb-5 inline-flex items-center gap-2 rounded-full bg-[#FFD60A] px-4 py-1.5 text-sm font-bold text-black" data-testid="join-invited-by">
                <UserPlus className="h-4 w-4" /> {t({ en: `${referrer.name} invited you`, bn: `${referrer.name} আপনাকে আমন্ত্রণ জানিয়েছেন` })}
              </div>
            )}
            <p className="font-join mb-4 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.3em] text-party-green"><Sparkles className="h-4 w-4" /> {t({ en: 'Membership Drive 2026', bn: 'সদস্যপদ অভিযান ২০২৬' })}</p>
            <h1 className="font-join text-5xl font-black leading-[0.95] sm:text-6xl lg:text-7xl" data-testid="join-headline">
              {t({ en: 'Your voice.', bn: 'আপনার কণ্ঠ।' })}<br /><span className="text-party-red">{t({ en: 'Your NDP.', bn: 'আপনার এনডিপি।' })}</span>
            </h1>
            <p className="mt-6 max-w-lg text-base text-gray-600 sm:text-lg">{t({ en: "Gen Z isn't waiting for change — we're building it. Join in 2 minutes, get your digital member ID instantly, and bring your squad.", bn: 'জেন জি পরিবর্তনের অপেক্ষায় নেই — আমরা তা গড়ছি। ২ মিনিটে যোগ দিন, তাৎক্ষণিক ডিজিটাল আইডি নিন, বন্ধুদের নিয়ে আসুন।' })}</p>
            <div className="mt-8"><StatPills stats={stats} /></div>
            <button onClick={() => navigate('/member-card')} className="mt-6 inline-flex items-center gap-2 rounded-full border border-gray-300 px-5 py-2.5 text-sm font-semibold text-gray-800 transition-colors duration-200 hover:bg-gray-900 hover:text-white" data-testid="join-download-id-btn">
              <CreditCard className="h-4 w-4" /> {t({ en: 'Already a member? Download ID Card', bn: 'ইতিমধ্যে সদস্য? আইডি কার্ড ডাউনলোড করুন' })}
            </button>
          </div>
          <div className="join-reveal min-w-0 [animation-delay:150ms]">
            {done ? <JoinSuccess data={done} /> : <><JoinForm refCode={referrer ? refCode : ''} onSuccess={setDone} initialDistrict={params.get('district') || ''} /><ScanPayCard /></>}
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-5">
        <PerksSection />
        <section className="grid gap-6 pb-20 lg:grid-cols-3">
          <Leaderboard board={board} />
          <CampusLeaderboard />
          <div className="rounded-3xl border border-gray-200 bg-gradient-to-br from-red-50 to-green-50 p-6" data-testid="campus-poster-block">
            <h3 className="font-join text-xl font-bold">{t({ en: 'Take it to your campus', bn: 'আপনার ক্যাম্পাসে ছড়িয়ে দিন' })}</h3>
            <p className="mt-2 mb-6 text-sm text-gray-600">{t({ en: 'Print the QR poster for notice boards, tea stalls, rallies & halls. Anyone scanning lands right on this page.', bn: 'নোটিশ বোর্ড, চায়ের দোকান, সমাবেশ ও হলের জন্য কিউআর পোস্টার প্রিন্ট করুন।' })}</p>
            <QRPoster refCode={done?.member_number || (referrer ? refCode : '')} />
          </div>
        </section>
      </div>
    </div>
  );
}
